import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environments';
import { setInstructorCorporateMode } from '../core/branding/instructor-branding.config';

export interface PlatformFeatures {
  corporateMode: boolean;
  companyPortal: boolean;
  parentPortal: boolean;
  gamification: boolean;
  assessment: boolean;
  liveClasses: boolean;
  scorm: boolean;
  advancedAnalytics: boolean;
  aiAuthoring: boolean;
}

const DISABLED_FEATURES: PlatformFeatures = {
  corporateMode: false,
  companyPortal: false,
  parentPortal: false,
  gamification: false,
  assessment: false,
  liveClasses: false,
  scorm: false,
  advancedAnalytics: false,
  aiAuthoring: false,
};

@Injectable({ providedIn: 'root' })
export class PlatformFeaturesService {
  private readonly featuresSubject = new BehaviorSubject<PlatformFeatures>(DISABLED_FEATURES);
  private loaded = false;

  constructor(private http: HttpClient) {}

  load(): Observable<PlatformFeatures> {
    if (this.loaded) {
      return of(this.featuresSubject.value);
    }

    return this.refresh();
  }

  refresh(): Observable<PlatformFeatures> {
    return this.http.get<any>(`${environment.baseUrl}${environment.systemConfiguration.platformFeatures}`).pipe(
      map((response) => this.toFeatures(response?.data)),
      tap((features) => this.applyFeatures(features)),
      catchError(() => {
        this.applyFeatures(DISABLED_FEATURES, false);
        return of(DISABLED_FEATURES);
      }),
    );
  }

  private applyFeatures(features: PlatformFeatures, markLoaded = true): void {
    const changed = this.loaded
      && JSON.stringify(this.featuresSubject.value) !== JSON.stringify(features);

    this.featuresSubject.next(features);
    setInstructorCorporateMode(features.corporateMode);
    this.loaded = markLoaded;

    if (changed) {
      window.location.reload();
    }
  }

  getParentPortalEnabled(): boolean {
    return this.featuresSubject.value.parentPortal;
  }

  getCompanyPortalEnabled(): boolean {
    return this.featuresSubject.value.companyPortal;
  }

  isEnabled(feature: keyof PlatformFeatures): boolean {
    return this.featuresSubject.value[feature];
  }

  features$(): Observable<PlatformFeatures> {
    return this.featuresSubject.asObservable();
  }

  private toFeatures(data: any): PlatformFeatures {
    return {
      corporateMode: data?.corporateMode === true,
      companyPortal: data?.companyPortal === true,
      parentPortal: data?.parentPortal === true,
      gamification: data?.gamification === true,
      assessment: data?.assessment === true,
      liveClasses: data?.liveClasses === true,
      scorm: data?.scorm === true,
      advancedAnalytics: data?.advancedAnalytics === true,
      aiAuthoring: data?.aiAuthoring === true,
    };
  }
}
